/**
 * Harness-neutral types for agent query execution.
 *
 * These types abstract the underlying agent SDK so that the runner,
 * execution, and tool layers never reference SDK-specific imports
 * directly.  The only place that should import from
 * `@anthropic-ai/claude-agent-sdk` is `harness/claude-code/`.
 */

import type { z } from "zod";

// ── Events emitted by the harness during a query ────────────────────────

export interface HarnessSystemInitEvent {
  type: "system";
  subtype: "init";
  session_id?: string;
  model: string;
}

export interface HarnessCompactBoundaryEvent {
  type: "system";
  subtype: "compact_boundary";
  compact_metadata: { trigger: "manual" | "auto"; pre_tokens: number };
}

export interface HarnessTaskStartedEvent {
  type: "system";
  subtype: "task_started";
  task_id: string;
  description: string;
}

export interface HarnessTaskProgressEvent {
  type: "system";
  subtype: "task_progress";
  task_id: string;
  description: string;
  usage?: { tool_uses: number; duration_ms: number };
}

export type HarnessSystemEvent =
  | HarnessSystemInitEvent
  | HarnessCompactBoundaryEvent
  | HarnessTaskStartedEvent
  | HarnessTaskProgressEvent;

export interface HarnessContentBlock {
  type: string;
  text?: string;
  name?: string;
  input?: unknown;
  id?: string;
}

export interface HarnessAssistantEvent {
  type: "assistant";
  message: {
    role: "assistant";
    content: HarnessContentBlock[];
    usage?: {
      input_tokens?: number;
      cache_read_input_tokens?: number;
      cache_creation_input_tokens?: number;
    };
  };
}

export interface HarnessResultSuccessEvent {
  type: "result";
  subtype: "success";
  result: string;
  total_cost_usd: number;
  modelUsage?: Record<string, unknown>;
  sessionId?: string;
}

export interface HarnessResultErrorEvent {
  type: "result";
  subtype: "error";
  errors: string[];
  sessionId?: string;
}

export type HarnessResultEvent = HarnessResultSuccessEvent | HarnessResultErrorEvent;

export interface HarnessRateLimitEvent {
  type: "rate_limit_event";
  rate_limit_info: {
    status: string;
    rateLimitType?: string;
    utilization?: number;
    resetsAt?: unknown;
  };
}

export interface HarnessToolProgressEvent {
  type: "tool_progress";
  tool_name?: string;
  elapsed_time_seconds?: number;
}

export interface HarnessUserQuestion {
  question: string;
  header: string;
  options: { label: string; description: string }[];
  multiSelect?: boolean;
}

/**
 * PTY harness only: the interactive CLI is about to render an AskUserQuestion
 * questionnaire (observed via the PreToolUse hook). The TUI collects the
 * answer natively — this event exists so the runner can report
 * `waiting_for_input` while the questionnaire is pending. The SDK harness
 * never emits it (AskUserQuestion routes through `canUseTool` there).
 */
export interface HarnessUserQuestionEvent {
  type: "user_question";
  questions: HarnessUserQuestion[];
}

export type HarnessEvent =
  | HarnessSystemEvent
  | HarnessAssistantEvent
  | HarnessResultEvent
  | HarnessRateLimitEvent
  | HarnessToolProgressEvent
  | HarnessUserQuestionEvent;

// ── User message fed into a query ───────────────────────────────────────

export interface HarnessUserMessage {
  type: "user";
  session_id: string;
  message: { role: "user"; content: string | unknown[] };
  parent_tool_use_id: null;
}

// ── Tool definition (harness-neutral) ───────────────────────────────────

export interface HarnessToolAnnotations {
  readOnlyHint?: boolean;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- generic tool handler */
export interface HarnessToolDefinition {
  name: string;
  description: string;
  schema: z.ZodRawShape;
  handler: (
    input: any,
  ) => Promise<{ content: { type: string; text?: string; data?: string; mimeType?: string }[] }>;
  annotations?: HarnessToolAnnotations;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ── Hook types ──────────────────────────────────────────────────────────

export interface HarnessHookInput {
  hook_event_name: string;
  tool_name: string;
  tool_response: unknown;
}

export interface HarnessHookOutput {
  continue: boolean;
}

export type HarnessPostToolUseHook = (input: HarnessHookInput) => Promise<HarnessHookOutput>;

// ── MCP server handle (opaque to the runner) ────────────────────────────

/** Opaque handle returned by `AgentHarness.createMcpServer()`. */
export type HarnessMcpServer = unknown;

// ── PTY relay bridge ────────────────────────────────────────────────────

/**
 * Bidirectional bridge between a PTY harness session and the S2 server relay.
 *
 * Kept harness-neutral (no AgentConnection import) so this module never depends
 * on the connection layer. Only the PTY harness consumes it; the SDK harness
 * ignores it. The runner adapts an `AgentConnection` into this shape.
 */
export interface PtyBridge {
  /** Forward a raw chunk of terminal output to the relay (fire-and-forget). */
  sendOutput(data: string, dims?: { cols: number; rows: number }): void;
  /** Subscribe to user keystrokes forwarded from the relay. Returns unsubscribe. */
  onInput(handler: (data: string) => void): () => void;
  /** Subscribe to reconciled terminal-resize events from the relay. Returns unsubscribe. */
  onResize(handler: (cols: number, rows: number) => void): () => void;
}

// ── Query options ───────────────────────────────────────────────────────

export interface HarnessQueryOptions {
  model: string;
  systemPrompt: unknown;
  cwd: string;
  permissionMode: "plan" | "bypassPermissions";
  allowDangerouslySkipPermissions: boolean;
  tools: { type: "preset"; preset: "claude_code" };
  mcpServers: Record<string, HarnessMcpServer>;
  settingSources?: ("user" | "project" | "local")[];
  sandbox?: { enabled: boolean };
  maxTurns?: number;
  maxBudgetUsd?: number;
  effort?: string;
  thinking?: unknown;
  betas?: unknown;
  disallowedTools?: string[];
  abortController?: AbortController;
  enableFileCheckpointing?: boolean;
  canUseTool?: (
    toolName: string,
    input: Record<string, unknown>,
  ) => Promise<
    | { behavior: "allow"; updatedInput?: Record<string, unknown> }
    | { behavior: "deny"; message: string }
  >;
  hooks?: Record<string, { hooks: HarnessPostToolUseHook[]; timeout: number }[]>;
  resume?: string;
  sessionId?: string;
  stderr?: (data: string) => void;
  /**
   * How the PTY harness delivers the query prompt to the spawned CLI:
   *   - "submit" (default): paste the prompt and press Enter — the turn runs
   *     immediately (auto-mode tasks, follow-up chat messages, resumes).
   *   - "prefill": paste the prompt into the TUI input WITHOUT submitting, so
   *     a human at the Connected-TUI terminal can review/edit/Enter it
   *     (manual-mode initial instructions).
   * The SDK harness has no interactive input box and ignores this option.
   */
  promptDelivery?: "submit" | "prefill";
  /**
   * Extra system-prompt text passed to the spawned CLI via
   * `--append-system-prompt`. PTY harness only — the SDK harness already
   * receives the full system prompt through `systemPrompt.append` and ignores
   * this field.
   */
  appendSystemPrompt?: string;
  /**
   * PTY harness only: after the PreToolUse hook allows an ExitPlanMode call,
   * the interactive CLI still shows the plan-approval dialog (a hook "allow"
   * does not bypass it). When set, the session presses Enter to accept the
   * default option ("Yes, auto-accept edits") so an autonomous agent
   * continues building in the same session without a human at the terminal.
   * Conveyor's validation already happened in the hook — the dialog is
   * residual UX at that point.
   */
  planDialogAutoAccept?: boolean;
}

// ── Tool definition helper ──────────────────────────────────────────────

/**
 * Construct a `HarnessToolDefinition` with the same signature as the SDK's
 * `tool()` helper, but without importing the SDK.  The harness implementation
 * converts these into SDK-native tools when `createMcpServer()` is called.
 */
export function defineTool<T extends z.ZodRawShape>(
  name: string,
  description: string,
  schema: T,
  handler: (
    input: z.infer<z.ZodObject<T>>,
  ) => Promise<{ content: { type: string; text?: string; data?: string; mimeType?: string }[] }>,
  options?: { annotations?: HarnessToolAnnotations },
): HarnessToolDefinition {
  return {
    name,
    description,
    schema,
    handler: handler as HarnessToolDefinition["handler"],
    annotations: options?.annotations,
  };
}

// ── AgentHarness interface ──────────────────────────────────────────────

export interface AgentHarness {
  /** Start a streaming query and return an async generator of events. */
  executeQuery(options: {
    prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>;
    options: HarnessQueryOptions;
    resume?: string;
  }): AsyncGenerator<HarnessEvent, void>;

  /** Wrap an array of harness-neutral tool definitions into an MCP server. */
  createMcpServer(config: { name: string; tools: HarnessToolDefinition[] }): HarnessMcpServer;
}
